import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface ErrorModalState {
  isOpen: boolean;
  title: string;
  message: string;
  traceId?: string;
}

interface ErrorContextType {
  errorModal: ErrorModalState;
  showErrorModal: (title: string, message: string, traceId?: string) => void;
  closeErrorModal: () => void;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

export const ErrorProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [errorModal, setErrorModal] = useState<ErrorModalState>({
    isOpen: false,
    title: '',
    message: '',
    traceId: undefined,
  });

  const showErrorModal = useCallback((title: string, message: string, traceId?: string) => {
    setErrorModal({
      isOpen: true,
      title,
      message,
      traceId,
    });
  }, []);

  const closeErrorModal = useCallback(() => {
    setErrorModal({
      isOpen: false,
      title: '',
      message: '',
      traceId: undefined,
    });
  }, []);

  return (
    <ErrorContext.Provider value={{ errorModal, showErrorModal, closeErrorModal }}>
      {children}
    </ErrorContext.Provider>
  );
};

export const useErrorModal = () => {
  const context = useContext(ErrorContext);
  if (context === undefined) {
    throw new Error('useErrorModal must be used within an ErrorProvider');
  }
  return context;
};
